<script module lang="ts">
  import type { Snippet } from 'svelte';
  import { type AllowedState } from './states.types';

  export interface Props {
    /** h1 … h6; defaults to the content layer the headline sits in, so nested boxes get h2, h3, … by themselves. */
    size?: number;
    state?: AllowedState;
    /** At the right end of the line, beside the title. */
    end?: Snippet;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { allowedStates } from './states.types';
  import { LayerHandler } from '../../handlers/LayerHandler';

  // -
  const { size, state: colorState, end, children, ...toNext }: Props = $props();

  // -
  const UseState = $derived(colorState && allowedStates.includes(colorState) ? colorState : undefined);
  const UseColor = $derived(UseState ? 'color-' + UseState : 'layout-front');

  // -
  const Layer = new LayerHandler();
  const MyLayer = Layer.upContentLayer();

  // -
  // - h1 … h6 follows the headline's own content layer unless `size` overrides it
  const UseSize = $derived(Math.max(1, Math.min(Math.round(size ?? MyLayer), 6)));
  const UseTag = $derived('h' + String(UseSize));

  // -
  const SizeMapper: Record<number, number> = {
    1: 2,
    2: 1.5,
    3: 1.25,
  };
  const UseSizeFactor = $derived(SizeMapper[UseSize] ?? 1);
</script>

<header {...toNext} class="headLineHeader contentLayer contentLayer{MyLayer}" class:hasEnd={end}>
  <svelte:element this={UseTag} class="headLine" style:--color-font={`var(--${UseColor}-accent)`} style:--font-size-factor={UseSizeFactor}>
    {@render children?.()}
  </svelte:element>
  {@render end?.()}
</header>

<style lang="scss">
  .hasEnd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .headLine {
    font-weight: 900;
    font-size: calc(var(--font-size) * var(--font-size-factor));
    color: var(--color-font);
  }
</style>
